import { useState } from "react";
import { Plus, Trash2, Upload, Users } from "lucide-react";
import { toast } from "sonner";
import { Card, EmptyState, ErrorBanner, PageHeader, Spinner, StatusBadge, fmt, useLoad } from "../../components/kit";
import { ImportDialog, ContactDialog } from "../../components/ContactDialogs";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { api, errMsg } from "../../lib/api";
import { useAuth } from "../../lib/auth";
import { useT } from "../../lib/i18n";

export const L = {
  en: { title: "Contacts", desc: "Audiences are the lists your campaigns go to. Add people by hand, import a CSV file or use the API.", newAud: "New audience",
    name: "Audience name", create: "Create", none: "No audiences yet", noneBody: "Create an audience, then import a CSV or add contacts one by one.",
    contacts: "contacts", contact: "contact", add: "Add contact", import: "Import CSV", search: "Search by email", empty: "No contacts in this audience yet.",
    unsub: "unsubscribed", remove: "Delete audience", confirmDel: "Delete this audience and all its contacts?", prev: "Previous", next: "Next" },
  fi: { title: "Yhteystiedot", desc: "Yleisöt ovat listoja, joille kampanjasi lähtevät. Lisää ihmisiä käsin, tuo CSV-tiedosto tai käytä API:a.", newAud: "Uusi yleisö",
    name: "Yleisön nimi", create: "Luo", none: "Ei vielä yleisöjä", noneBody: "Luo yleisö ja tuo sitten CSV-tiedosto tai lisää yhteystiedot yksitellen.",
    contacts: "yhteystietoa", contact: "yhteystieto", add: "Lisää yhteystieto", import: "Tuo CSV", search: "Hae sähköpostilla", empty: "Tässä yleisössä ei ole vielä yhteystietoja.",
    unsub: "perunut tilauksen", remove: "Poista yleisö", confirmDel: "Poistetaanko yleisö ja kaikki sen yhteystiedot?", prev: "Edellinen", next: "Seuraava" },
};

function ContactTable({ aud, l, canEdit, onChanged }) {
  const [q, setQ] = useState("");
  const [page, setPage] = useState(1);
  const [dialog, setDialog] = useState(null);
  const { data, error, loading, reload: reloadList } = useLoad(() => api.get(`/app/audiences/${aud.id}/contacts`, { params: { q: q || undefined, page } }).then((r) => r.data), [aud.id, q, page]);
  const reload = () => { reloadList(); onChanged(); };
  const del = async (c) => {
    if (!window.confirm(`Delete ${c.email}?`)) return;
    try { await api.delete(`/app/audiences/${aud.id}/contacts/${c.id}`); toast.success("Contact deleted"); reload(); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <Card testId="contacts-panel">
      <div className="mb-5 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <Input value={q} onChange={(e) => { setQ(e.target.value); setPage(1); }} placeholder={l.search} className="sm:max-w-xs" data-testid="contacts-search" />
        {canEdit && <div className="flex gap-2">
          <Button variant="outline" onClick={() => setDialog("import")} className="border-white/10 bg-transparent" data-testid="contacts-import-button"><Upload className="mr-1 h-4 w-4" />{l.import}</Button>
          <Button onClick={() => setDialog("add")} data-testid="contacts-add-button"><Plus className="mr-1 h-4 w-4" />{l.add}</Button>
        </div>}
      </div>
      <ErrorBanner error={error} />
      {loading && !data ? <Spinner /> : data && (data.contacts.length === 0 ? <p className="text-sm text-zinc-500" data-testid="contacts-empty">{l.empty}</p> : (
        <div className="overflow-x-auto"><table className="w-full min-w-[560px] text-left text-sm">
          <thead className="text-xs uppercase tracking-wider text-zinc-500"><tr><th className="p-2.5">Email</th><th className="p-2.5">Name</th><th className="p-2.5">Fields</th><th className="p-2.5">Added</th><th /></tr></thead>
          <tbody>{data.contacts.map((c) => (
            <tr key={c.id} className="border-t border-white/5" data-testid={`contact-row-${c.email}`}>
              <td className="p-2.5 font-mono text-xs text-zinc-200">{c.email} {c.suppressed && <StatusBadge status="suppressed" testId="contact-suppressed" />}</td>
              <td className="p-2.5 text-zinc-300">{[c.first_name, c.last_name].filter(Boolean).join(" ") || "—"}</td>
              <td className="p-2.5 text-xs text-zinc-500">{Object.keys(c.data || {}).join(", ") || "—"}</td>
              <td className="p-2.5 text-xs text-zinc-500">{fmt(c.created_at)}</td>
              <td className="p-2.5 text-right">{canEdit && <Button size="sm" variant="ghost" onClick={() => del(c)} className="text-red-300 hover:bg-red-500/10" data-testid="contact-delete-button"><Trash2 className="h-4 w-4" /></Button>}</td>
            </tr>))}</tbody>
        </table></div>))}
      {data && data.total > 50 && (
        <div className="mt-4 flex items-center gap-3 text-xs text-zinc-400">
          <Button size="sm" variant="outline" disabled={page === 1} onClick={() => setPage(page - 1)} className="border-white/10 bg-transparent" data-testid="contacts-prev">{l.prev}</Button>
          <span data-testid="contacts-page">{page} / {Math.ceil(data.total / 50)}</span>
          <Button size="sm" variant="outline" disabled={page * 50 >= data.total} onClick={() => setPage(page + 1)} className="border-white/10 bg-transparent" data-testid="contacts-next">{l.next}</Button>
        </div>)}
      <ContactDialog open={dialog === "add"} onClose={() => setDialog(null)} audId={aud.id} onDone={reload} />
      <ImportDialog open={dialog === "import"} onClose={() => setDialog(null)} audId={aud.id} onDone={reload} />
    </Card>
  );
}

function NewAudience({ l, onCreated }) {
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault(); setBusy(true);
    try { const { data } = await api.post("/app/audiences", { name }); setName(""); onCreated(data); toast.success("Audience created"); }
    catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };
  return (
    <form onSubmit={submit} className="flex gap-2">
      <Input required value={name} onChange={(e) => setName(e.target.value)} placeholder={l.name} className="w-56" data-testid="audience-name-input" />
      <Button disabled={busy} data-testid="audience-create-button"><Plus className="mr-1 h-4 w-4" />{l.create}</Button>
    </form>
  );
}

export default function Audiences() {
  const { lang } = useT();
  const l = L[lang] || L.en;
  const { can } = useAuth();
  const canEdit = can("developer");
  const [sel, setSel] = useState(null);
  const { data, error, loading, reload } = useLoad(() => api.get("/app/audiences").then((r) => r.data), []);
  const current = data?.find((a) => a.id === sel) || data?.[0];
  const remove = async () => {
    if (!window.confirm(l.confirmDel)) return;
    try { await api.delete(`/app/audiences/${current.id}`); setSel(null); reload(); toast.success("Audience deleted"); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div>
      <PageHeader eyebrow="Marketing" title={l.title} description={l.desc} actions={canEdit && <NewAudience l={l} onCreated={(a) => { reload(); setSel(a.id); }} />} />
      <ErrorBanner error={error} />
      {loading && !data ? <Spinner /> : data && (data.length === 0 ? <EmptyState icon={Users} title={l.none} body={l.noneBody} testId="audiences-empty" /> : (
        <div className="grid gap-6 lg:grid-cols-[260px_1fr]">
          <div className="space-y-2" data-testid="audience-list">
            {data.map((a) => (
              <button key={a.id} type="button" onClick={() => setSel(a.id)} data-testid={`audience-item-${a.id}`}
                className={`w-full rounded-xl border p-4 text-left transition-colors ${current?.id === a.id ? "border-purple-400/40 bg-purple-500/10" : "border-white/[0.06] bg-white/[0.02] hover:bg-white/[0.04]"}`}>
                <div className="font-medium text-white">{a.name}</div>
                <div className="mt-1 text-xs text-zinc-500" data-testid="audience-count">{a.contacts.toLocaleString()} {a.contacts === 1 ? l.contact : l.contacts}</div>
              </button>))}
            {can("admin") && current && <Button variant="ghost" size="sm" onClick={remove} className="text-red-300 hover:bg-red-500/10" data-testid="audience-delete-button"><Trash2 className="mr-1 h-4 w-4" />{l.remove}</Button>}
          </div>
          {current && <ContactTable key={current.id} aud={current} l={l} canEdit={canEdit} onChanged={reload} />}
        </div>))}
    </div>
  );
}
